/**
 * Cierre del ciclo de Tab del Dialog v2 dentro del `role="dialog"` (TAN-11097).
 *
 * Por qué existe: el `TrapFocus` de MUI v4 (`@material-ui/core/Unstable_TrapFocus`)
 * rodea el `.MuiDialog-container` con dos centinelas `tabIndex=0`. Al tabular desde
 * el último control el navegador enfoca el centinela final y el listener `contain`
 * de MUI devuelve el foco al contenedor (`tabindex="-1"`, sin indicador visible);
 * solo en la pulsación siguiente llega al primer control. Aquí, en los bordes del
 * ciclo, se salta directamente al primer/último tabulable. El trap de MUI sigue
 * activo tal cual (contención y retorno de foco), sin tocarlo.
 *
 * Límite conocido: si el foco está en un `<iframe>` o en un reproductor
 * (`<audio controls>`/`<video controls>`) situado en el borde del ciclo, no se
 * interviene. El keydown de un iframe no llega a este documento, y los controles
 * internos del reproductor viven en el shadow DOM del navegador (el keydown llega
 * con `target` = el propio `<video>`), así que no se puede saber si el Tab sale
 * del reproductor o se mueve dentro de él. En ese caso el navegador sigue con su
 * Tab nativo: se alcanzan todos los controles, a costa de conservar las paradas
 * de MUI en ese borde.
 */

const TABBABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'iframe',
  'audio[controls]',
  'video[controls]',
  'details',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');

type FocusCycleDirection = 'forward' | 'backward';

// Elementos que el navegador pone en el orden de Tab aunque su `tabIndex` por
// defecto sea -1 (HTML Standard: el valor por defecto es 0 solo para a, area,
// button, iframe, input, select, textarea, summary…). Misma regla que `getTabIndex`
// de la librería `tabbable`: sin atributo `tabindex`, cuentan como 0.
const DEFAULT_TABBABLE_TAGS = new Set(['AUDIO', 'VIDEO', 'DETAILS']);

function isContentEditable(element: HTMLElement): boolean {
  const value = element.getAttribute('contenteditable');
  return value === '' || value === 'true' || value === 'plaintext-only';
}

function getTabIndex(element: HTMLElement): number {
  if (element.tabIndex >= 0 || element.hasAttribute('tabindex')) return element.tabIndex;
  return DEFAULT_TABBABLE_TAGS.has(element.tagName) || isContentEditable(element) ? 0 : element.tabIndex;
}

// Un `<details>` con `<summary>` no es parada: la parada es su `<summary>`.
function isDetailsWithSummary(element: HTMLElement): boolean {
  return element.tagName === 'DETAILS' && Array.from(element.children).some((child) => child.tagName === 'SUMMARY');
}

// Reproductores y marcos gestionan su propio Tab interno (ver cabecera).
function isOpaqueToTab(element: HTMLElement): boolean {
  return element.tagName === 'IFRAME' || ((element.tagName === 'AUDIO' || element.tagName === 'VIDEO') && element.hasAttribute('controls'));
}

// `:disabled` cubre también los controles dentro de un `<fieldset disabled>`
// (RadioGroup/CheckboxGroup del DS), que no reflejan la propiedad `.disabled`.
function isDisabled(element: HTMLElement): boolean {
  return element.matches(':disabled');
}

// Sin layout en jsdom (getClientRects vacío), la visibilidad se decide por estilos
// calculados de la cadena de ancestros hasta el Paper; vale igual en navegador.
function isRendered(element: HTMLElement, container: HTMLElement): boolean {
  if (globalThis.getComputedStyle(element).visibility === 'hidden') return false;
  let node: HTMLElement | null = element;
  while (node && node !== container) {
    if (node.hidden || node.hasAttribute('inert') || globalThis.getComputedStyle(node).display === 'none') {
      return false;
    }
    node = node.parentElement;
  }
  return true;
}

function isTabbable(element: HTMLElement, container: HTMLElement): boolean {
  return getTabIndex(element) >= 0 && !isDetailsWithSummary(element) && !isDisabled(element) && isRendered(element, container);
}

function getRadioGroupKey(element: HTMLElement): string | null {
  if (!(element instanceof HTMLInputElement) || element.type !== 'radio' || !element.name) return null;
  return element.name;
}

function isSameRadioGroup(a: HTMLElement, b: HTMLElement): boolean {
  const key = getRadioGroupKey(a);
  return key !== null && key === getRadioGroupKey(b);
}

/**
 * Primera parada de Tab recorriendo `candidates` en el orden dado. Se ignoran
 * `current` y su propio grupo de radios (Tab no vuelve a ellos). Los ancestros
 * tabulables de `current` sí cuentan: van antes que sus hijos en el orden de Tab,
 * así que Shift+Tab desde el primer hijo vuelve a ellos. Se corta en la primera coincidencia para no calcular
 * estilos del diálogo entero en cada pulsación.
 */
function findStop(candidates: HTMLElement[], container: HTMLElement, current: HTMLElement): HTMLElement | null {
  return candidates.find((element) => element !== current && !isSameRadioGroup(element, current) && isTabbable(element, container)) ?? null;
}

/**
 * Un grupo de radios con `name` es una sola parada: el navegador entra por el
 * radio marcado o, si no hay ninguno, por el primero (Tab) o el último (Shift+Tab).
 */
function resolveRadioEntry(element: HTMLElement, container: HTMLElement, direction: FocusCycleDirection): HTMLElement {
  const key = getRadioGroupKey(element);
  if (!key) return element;
  const group = Array.from(container.querySelectorAll<HTMLInputElement>('input[type="radio"]')).filter(
    (radio) => radio.name === key && isTabbable(radio, container)
  );
  const checked = group.find((radio) => radio.checked);
  return checked ?? (direction === 'forward' ? group[0] : group.at(-1)) ?? element;
}

function isAfter(reference: Node, element: Node): boolean {
  return Boolean(reference.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);
}

/**
 * Devuelve el elemento al que hay que llevar el foco para cerrar el ciclo, o `null`
 * si el Tab debe seguir su curso natural (hay más paradas en esa dirección, o
 * `current` es un iframe/reproductor). Si no hay otra parada a la que ir (ningún
 * tabulable, o `current` es el único), devuelve `current`: el foco se queda donde
 * está en vez de salir al contenedor.
 */
function getFocusCycleTarget(container: HTMLElement, current: HTMLElement, direction: FocusCycleDirection): HTMLElement | null {
  if (isOpaqueToTab(current)) return null;

  const inDocumentOrder = Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR));
  const inReverseOrder = [...inDocumentOrder].reverse();
  const isForward = direction === 'forward';

  // Borde del ciclo en la dirección del Tab: si aún queda una parada más allá de
  // `current`, el navegador la alcanza solo y no intervenimos.
  const edge = findStop(isForward ? inReverseOrder : inDocumentOrder, container, current);
  if (!edge) return current;
  const isEdgeAhead = isForward ? isAfter(current, edge) : isAfter(edge, current);
  if (isEdgeAhead) return null;

  // Estamos en el borde: saltamos al extremo opuesto del ciclo. `?? edge` solo
  // estrecha el tipo: `edge` cumple el mismo predicado, así que nunca es null.
  const wrapTarget = findStop(isForward ? inDocumentOrder : inReverseOrder, container, current) ?? edge;
  return resolveRadioEntry(wrapTarget, container, direction);
}

export type { FocusCycleDirection };
export { getFocusCycleTarget };
